import type {
  BuildStamp,
  FindingEnvironment,
  QaContext,
} from "../shared/schemas";
import { getCaptureFrom } from "./console-tail";

/**
 * Automatic context capture. Everything here is best-effort — a finding must
 * never fail to save because a context probe did.
 *
 * Ported from qa-deck's `src/widget/environment.ts`. The one product-specific
 * part had to go: the donor read zuperinvoice's anonymous-session key out of
 * localStorage and forwarded it as a header so the product's own QA endpoint
 * could report an anonymous visitor correctly. A generic tenant has no such
 * key, so the probe is now a plain credentialed GET of an OPTIONAL endpoint the
 * customer may expose. Most won't, and the field is simply omitted.
 */

let appName = "app";
let qaContextPath: string | null = "/api/qa-context";

export function setAppName(name: string): void {
  appName = name;
}

/**
 * Point the optional tier/flag probe somewhere else, or switch it off entirely
 * (`null`). Set from the script tag's `data-qa-deck-context` attribute.
 */
export function setQaContextPath(path: string | null): void {
  qaContextPath = path;
}

function envLabel(): string {
  const host = location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
    return "dev";
  }
  if (host.includes("staging")) return "staging";
  return host;
}

/**
 * The build this page is running, as the consumer's probe reported it.
 *
 * Cached rather than re-read: a document load pins one build, and the panel
 * needs the answer to render a retest row long before anybody files a finding.
 * The TIER is deliberately not cached beside it — a tester can sign in as
 * somebody else mid-session, and stale flags on a finding are evidence that
 * lies. So the probe is still fetched fresh at filing time; it just leaves the
 * build behind on its way past.
 */
let currentBuild: CurrentBuild | null = null;

/**
 * The build on screen, plus what the consumer says it CONTAINS.
 *
 * `contains` lives here and nowhere else: it is read off the probe for the
 * comparison the panel makes on this page, and never stored on a finding — a
 * finding records the one build it was filed on, and a list of two hundred
 * shas on every row would be weight with no reader.
 */
export type CurrentBuild = BuildStamp & { contains: string[] };

/** Bounds on a third party's list: enough builds to cover weeks of deploys,
 *  never enough to matter in memory. */
const MAX_CONTAINS = 500;
const MAX_STAMP_CHARS = 200;

/** What the panel compares a fix claim against. Null ⇒ the consumer exposes no
 *  probe, or none that names a build: nothing is claimed either way. */
export function getBuild(): CurrentBuild | null {
  return currentBuild;
}

/**
 * Learn the build once, without waiting for a finding to be filed.
 *
 * Best-effort like everything else here: a probe that 404s (the normal case)
 * leaves the build null and every caller degrades to the behaviour it had
 * before builds existed.
 */
export async function ensureBuild(): Promise<void> {
  if (currentBuild || !qaContextPath) return;
  await fetchQaContext();
}

/** A build stamp off a third party's JSON. Same rule as the tier below: shape
 *  -checked here, because the stamp ends up in our DB. */
function toBuild(value: unknown): CurrentBuild | undefined {
  if (!value || typeof value !== "object") return undefined;
  const sha = (value as { sha?: unknown }).sha;
  if (typeof sha !== "string" || !sha) return undefined;
  const builtAt = (value as { builtAt?: unknown }).builtAt;
  const contains = (value as { contains?: unknown }).contains;
  return {
    sha: sha.slice(0, MAX_STAMP_CHARS),
    builtAt: typeof builtAt === "string" ? builtAt.slice(0, 60) : null,
    // Optional, and only strings: the consumer's git wrote this list, but it
    // still arrived through a third party's endpoint.
    contains: Array.isArray(contains)
      ? contains
          .filter((c): c is string => typeof c === "string" && c.length > 0)
          .slice(0, MAX_CONTAINS)
          .map((c) => c.slice(0, MAX_STAMP_CHARS))
      : [],
  };
}

/**
 * Effective tier + feature-flag state from the CUSTOMER's own QA-context
 * endpoint, when they expose one. A 404 (the normal case) means the field is
 * omitted — never that capture fails.
 */
async function fetchQaContext(): Promise<QaContext | undefined> {
  if (!qaContextPath) return undefined;
  try {
    const response = await fetch(`${location.origin}${qaContextPath}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return undefined;
    const body = (await response.json()) as Partial<QaContext> & {
      build?: unknown;
    };
    // The build is read even when the tier is not: they answer different
    // questions, and a consumer may reasonably ship one and not the other.
    const build = toBuild(body?.build);
    if (build) currentBuild = build;
    // Shape-check here rather than trusting a third party's endpoint: this JSON
    // comes from the customer's app, not from us, and it ends up in our DB.
    if (typeof body?.effectiveTier !== "string") return undefined;
    return {
      effectiveTier: body.effectiveTier,
      userRole: typeof body.userRole === "string" ? body.userRole : null,
      isAnonymous: body.isAnonymous === true,
      flags: Object.fromEntries(
        Object.entries(body.flags ?? {}).map(([k, v]) => [k, v === true]),
      ),
    };
  } catch {
    return undefined;
  }
}

/**
 * Session-replay URL, if the page happens to expose a tracker. Read-only and
 * entirely opportunistic — by design we do not build session capture here.
 */
function openReplayUrl(): string | null {
  const w = window as unknown as {
    tracker?: { getSessionURL?: () => string };
    __OPENREPLAY_TRACKER__?: { getSessionURL?: () => string };
  };
  try {
    return (
      w.tracker?.getSessionURL?.() ??
      w.__OPENREPLAY_TRACKER__?.getSessionURL?.() ??
      null
    );
  } catch {
    return null;
  }
}

export interface CapturedContext {
  environment: FindingEnvironment;
  openreplay_url: string | null;
}

export async function captureContext(): Promise<CapturedContext> {
  const environment: FindingEnvironment = {
    viewport: { w: window.innerWidth, h: window.innerHeight },
    userAgent: navigator.userAgent,
    // The console ring buffer starts at widget load: an empty tail is NOT
    // evidence of a clean console, and this timestamp is what says so.
    consoleCaptureFrom: getCaptureFrom(),
    app: appName,
    env: envLabel(),
  };

  const qaContext = await fetchQaContext();
  if (qaContext) environment.qaContext = qaContext;
  // Beside `env`, not inside `qaContext`: "which build was on screen" is a fact
  // about the deployment, and a consumer that reports a build but no tier must
  // still get one. Set after the probe, so it carries this page's answer. The
  // STAMP only — what the build contains is for comparing, not for keeping.
  if (currentBuild) {
    environment.build = {
      sha: currentBuild.sha,
      builtAt: currentBuild.builtAt,
    };
  }

  return { environment, openreplay_url: openReplayUrl() };
}
